import { ERROR_MESSAGES } from '@src/utils/errorMessages';
import type { Editor } from '@tiptap/react';
import { useCallback, useMemo, useState } from 'react';

import { LinkUrlErrorResponse } from '../../components/modals';
import type { LinkUrlParams } from '../../types';

export interface LinkModalReturn {
  isLinkAddModalOpen: boolean;
  linkAddModalParams: LinkUrlParams;
  linkAddModalInitialParams?: LinkUrlParams;
  linkUrlErrors: LinkUrlErrorResponse;

  openLinkAddModal: () => void;
  closeLinkAddModal: () => void;
  setLinkAddModalParams: (params: LinkUrlParams) => void;
  setLinkUrlErrors: (errors: LinkUrlErrorResponse) => void;

  handleOpenLinkAddModal: () => void;
  handleCloseLinkAddModal: () => void;
  handleSubmitLink: (params: LinkUrlParams) => void;
  handleDeleteLink: (onClosePopup?: () => void) => void;
}

/**
 * useLinkModal
 * Unified hook for link modal state management + business logic
 */
export function useLinkModal(editor: Editor | null): LinkModalReturn {
  const defaultLinkUrlParams = useMemo<LinkUrlParams>(() => ({ url: '', text: '' }), []);
  const [isLinkAddModalOpen, setIsLinkAddModalOpen] = useState(false);
  const [linkAddModalParams, setLinkAddModalParams] = useState<LinkUrlParams>(defaultLinkUrlParams);
  const [linkAddModalInitialParams, setLinkAddModalInitialParams] = useState<
    LinkUrlParams | undefined
  >(undefined);
  const [linkUrlErrors, setLinkUrlErrors] = useState<LinkUrlErrorResponse>({});

  const openLinkAddModal = useCallback(() => {
    setIsLinkAddModalOpen(true);
  }, []);

  const closeLinkAddModal = useCallback(() => {
    setLinkAddModalParams(defaultLinkUrlParams);
    setLinkAddModalInitialParams(undefined);
    setLinkUrlErrors({});
    setIsLinkAddModalOpen(false);
  }, [defaultLinkUrlParams]);

  const handleOpenLinkAddModal = useCallback(() => {
    if (!editor) {
      return;
    }

    const { state } = editor;
    const { from, to } = state.selection;

    const selectedText = state.doc.textBetween(from, to, ' ');

    let containsMention = false;
    state.doc.nodesBetween(from, to, (node) => {
      const hasMentionMark = node.marks.some((mark) => mark.type.name === 'mention');
      if (hasMentionMark) {
        containsMention = true;
        return false;
      }
    });

    if (containsMention) {
      return;
    }

    const currentParams = {
      url: editor?.getAttributes('link').href || '',
      text: selectedText || linkAddModalParams.text || '',
    };

    setLinkAddModalParams(currentParams);
    setLinkAddModalInitialParams(currentParams);
    setIsLinkAddModalOpen(true);
  }, [editor, linkAddModalParams.text]);

  const handleCloseLinkAddModal = useCallback(() => {
    closeLinkAddModal();
    setLinkUrlErrors({});
  }, [closeLinkAddModal]);

  const handleSubmitLink = useCallback(
    (params: LinkUrlParams) => {
      if (!editor) {
        return;
      }

      const trimmedUrl = params.url.trim();
      const trimmedText = params.text.trim();
      const isEditingLink = editor.getAttributes('link').href;
      const isEmpty = trimmedUrl === '';

      if (isEditingLink && isEmpty) {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        handleCloseLinkAddModal();
        return;
      }

      if (!isEditingLink && isEmpty) {
        setLinkUrlErrors({
          url: [ERROR_MESSAGES.URL.REQUIRED],
        });
        return;
      }

      if (!isEmpty) {
        try {
          const parsed = new URL(trimmedUrl);
          if (!['http:', 'https:'].includes(parsed.protocol)) {
            throw new Error('invalid');
          }
        } catch {
          setLinkUrlErrors({
            url: [ERROR_MESSAGES.URL.INVALID_FORMAT],
          });
          return;
        }
      }

      const { from, to } = editor.state.selection;

      if (isEditingLink) {
        editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .deleteSelection()
          .insertContent([
            {
              type: 'text',
              text: trimmedText,
              marks: [{ type: 'link', attrs: { href: trimmedUrl } }],
            },
          ])
          .run();
      } else if (from !== to) {
        editor
          .chain()
          .focus()
          .deleteSelection()
          .insertContent([
            {
              type: 'text',
              text: trimmedText,
              marks: [{ type: 'link', attrs: { href: trimmedUrl } }],
            },
          ])
          .run();
      } else if (trimmedText) {
        editor
          .chain()
          .focus()
          .insertContent([
            {
              type: 'text',
              text: trimmedText,
              marks: [{ type: 'link', attrs: { href: trimmedUrl } }],
            },
          ])
          .run();
      }

      handleCloseLinkAddModal();
    },
    [editor, handleCloseLinkAddModal],
  );

  const handleDeleteLink = useCallback(
    (onClosePopup?: () => void) => {
      if (!editor) {
        return;
      }
      editor.commands.unsetLink();
      if (onClosePopup) {
        onClosePopup();
      }
    },
    [editor],
  );

  return {
    isLinkAddModalOpen,
    linkAddModalParams,
    linkAddModalInitialParams,
    linkUrlErrors,

    openLinkAddModal,
    closeLinkAddModal,
    setLinkAddModalParams,
    setLinkUrlErrors,

    handleOpenLinkAddModal,
    handleCloseLinkAddModal,
    handleSubmitLink,
    handleDeleteLink,
  };
}
